import React from "react";
import cronstrue from "cronstrue";

import { Button } from "react-admin";

import MuiButton from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import OutlinedInput from "@mui/material/OutlinedInput";
import Typography from "@mui/material/Typography";

const formatHour = (hour: number) => {
  if (hour === 0) {
    return "midnight";
  }
  if (hour < 12) {
    return String(hour) + " am";
  }
  if (hour === 12) {
    return "noon";
  }
  return String(hour - 12) + " pm";
};

const NewScheduleButton = ({
  disabled,
  setCronString,
  value,
}: {
  disabled?: boolean;
  setCronString: (cron: string) => void;
  value?: string;
}) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const [minutes, setMinutes] = React.useState<number>(
    Math.floor(Math.random() * 59),
  );
  const [hours, setHours] = React.useState<number[]>([]);

  const handleOpen = () => {
    setOpen(true);
    // Parse existing cron string to pre-populate hours and minutes
    if (value) {
      const fields = value.split(" ");
      if (fields.length >= 3) {
        const cronMinutes = parseInt(fields[1], 10);
        setMinutes(isNaN(cronMinutes) ? 0 : cronMinutes);
        const cronHours = fields[2]
          .split(",")
          .map((h) => {
            const parsed = parseInt(h, 10);
            return isNaN(parsed) ? -1 : parsed;
          })
          .filter((h) => h >= 0 && h <= 23);
        setHours(cronHours.length > 0 ? cronHours : []);
      }
    }
  };
  const handleCancel = () => {
    setOpen(false);
  };
  const handleSave = () => {
    setOpen(false);
    const cronArray = [
      "0", // seconds
      minutes,
      hours.join(","),
      "*",
      "*",
      "*",
    ];

    const cronString = cronArray.join(" ");
    setCronString(cronString);
  };

  return (
    <div>
      <Button
        label="Change"
        variant="contained"
        size="small"
        disabled={disabled}
        onClick={handleOpen}
      >
        <SettingsSuggestIcon />
      </Button>
      <Dialog open={open} fullWidth maxWidth="md">
        <DialogTitle>Pumpover Schedule</DialogTitle>
        <DialogContent>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body1">Run at</Typography>
            <FormControl variant="standard" sx={{ m: 1, minWidth: 40 }}>
              <Select
                size="small"
                value={minutes}
                onChange={(e) => {
                  const value = e.target.value;
                  setMinutes(
                    typeof value === "string" ? parseInt(value) : value,
                  );
                }}
                input={<OutlinedInput />}
                renderValue={(selected) => String(selected).padStart(2, "0")}
              >
                {Array.from(Array(60).keys()).map((minute) => (
                  <MenuItem key={minute} value={minute}>
                    {String(minute).padStart(2, "0")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body1">
              minutes past the hour, on the following hours:
            </Typography>
          </div>
          <br />
          <div
            style={{
              display: "grid",
              // Re-flow the 24 hour cells to fit the available width
              // (the previous fixed 24-column layout overflowed the
              // dialog on every screen size).
              gridTemplateColumns:
                "repeat(auto-fill, minmax(120px, 1fr))",
              alignItems: "center",
            }}
          >
            {Array.from(Array(24).keys()).map((hour) => (
              <label
                key={hour}
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <Checkbox
                  size="small"
                  checked={hours.indexOf(hour) > -1}
                  onChange={() => {
                    const newHours = hours.includes(hour)
                      ? hours.filter((h) => h !== hour)
                      : [...hours, hour].sort((a, b) => a - b);
                    setHours(newHours);
                  }}
                />
                <Typography variant="caption" style={{ marginLeft: 4 }}>
                  {String(hour).padStart(2, "0")} ({formatHour(hour)})
                </Typography>
              </label>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <MuiButton autoFocus onClick={handleCancel}>
            Cancel
          </MuiButton>
          <MuiButton onClick={handleSave} disabled={hours.length === 0}>
            Save
          </MuiButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const CronChooser = ({
  value,
  setValue,
  disabled,
}: {
  value?: string;
  setValue: (v: string) => void;
  disabled?: boolean;
}) => {
  return (
    <>
      <div
        style={{
          opacity: disabled ? 0.4 : 1.0,
        }}
      >
        {value
          ? cronstrue.toString(value, { use24HourTimeFormat: true })
          : "None"}
      </div>
      <NewScheduleButton
        disabled={disabled}
        setCronString={setValue}
        value={value}
      />
    </>
  );
};

export { CronChooser };
